import React, { useState, useEffect, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import { Modal } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  BoltOutlined as BoltIcon,
  Photo as PhotoIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as AssignmentIcon,
  AccessTime as TimeIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  Build as BuildIcon,
  CleaningServices as CleaningIcon,
  Warning as WarningIcon,
  CheckBox as CheckBoxIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  DateRange as DateRangeIcon,
  KeyboardArrowRight as ArrowRightIcon,
  Event as EventIcon,
  Engineering as EngineeringIcon,
  Notes as NotesIcon,
  Add as AddIcon
} from "@mui/icons-material";
import { format } from "date-fns";
import { getAuthHeader } from "../../helpers/auth";
import axios from "axios";

const JobHistory = () => {
  const config = getAuthHeader();
  const location = useLocation();

  const [detailsLoading, setDetailsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    scheduled: 0,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);

const JobEfficiencyTimer = ({ job }) => {
  const [timerData, setTimerData] = useState({ 
    remainingTime: 0, 
    isExceeded: false, 
    delayMinutes: 0,
    totalTimeTaken: 0,
    efficiencyScore: 0
  });

  useEffect(() => {
    const calculate = () => {
      if (!job.started_at) return;
      
      // 1. Fixed Shift Logic for Late Start
      const actualReached = new Date(job.reached_location_at);
      const shiftStartTime = new Date(actualReached); 
      
      if (job.shift_type === 'afternoon') {
        shiftStartTime.setHours(12, 0, 0, 0); // 12:00 PM
      } else {
        shiftStartTime.setHours(7, 0, 0, 0); // 7:00 AM
      }
      
      const diffMs = actualReached - shiftStartTime;
      const delay = Math.max(0, Math.floor(diffMs / 60000));
      
      // 2. Efficiency Logic
      const teamSize = 4;
      const totalManHours = parseFloat(job.total_man_hours) || 32;
      const targetSec = (totalManHours / teamSize) * 3600;
      
      const endTime = job.completed_at ? new Date(job.completed_at) : new Date();
      const elapsedSec = Math.floor((endTime - new Date(job.reached_location_at)) / 1000);
      const remaining = targetSec - elapsedSec;
      const efficiency = elapsedSec > 0 ? Math.round((targetSec / elapsedSec) * 100) : 100;

      setTimerData({ 
        remainingTime: Math.abs(remaining), 
        isExceeded: remaining < 0, 
        delayMinutes: delay,
        totalTimeTaken: elapsedSec,
        efficiencyScore: efficiency
      });
    };

    if (job.completed_at) {
      calculate();
    } else {
      const itv = setInterval(calculate, 1000);
      calculate();
      return () => clearInterval(itv);
    }
  }, [job]);

  // Formatter for HH:MM:SS
  const formatDisplayTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Naya Formatter for Late Start (Mins to Hours conversion)
  const formatDelay = (totalMins) => {
    if (totalMins < 60) return `${totalMins}m`;
    const h = Math.floor(totalMins / 60);
    const m = totalMins % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  if (!job.started_at) return (
    <div style={{ marginTop: '12px', padding: '12px', borderRadius: '12px', background: '#F3F4F6', textAlign: 'center', border: '1px dashed #D1D5DB' }}>
      <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '600' }}>🕒 Waiting to start...</span>
    </div>
  );

  const isDone = !!job.completed_at;

  return (
    <div style={{ 
      marginTop: '12px', 
      padding: '16px', 
      borderRadius: '16px', 
      background: isDone ? '#F8FAFC' : (timerData.isExceeded ? '#FFF1F2' : '#F0FDF4'), 
      border: `2px solid ${isDone ? '#E2E8F0' : (timerData.isExceeded ? '#FECACA' : '#BBF7D0')}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
        <div>
          <span style={{ fontSize: '10px', fontWeight: '800', color: isDone ? '#64748B' : (timerData.isExceeded ? '#E11D48' : '#166534'), textTransform: 'uppercase' }}>
            {isDone ? '🏁 Total Time Taken' : (timerData.isExceeded ? '⚠️ Overtime' : '⏱️ Time Remaining')}
          </span>
          <div style={{ fontSize: '24px', fontWeight: '900', color: isDone ? '#1E293B' : (timerData.isExceeded ? '#BE123C' : '#15803D'), fontFamily: 'monospace', lineHeight: 1 }}>
            {isDone ? formatDisplayTime(timerData.totalTimeTaken) : (timerData.isExceeded ? `-${formatDisplayTime(timerData.remainingTime)}` : formatDisplayTime(timerData.remainingTime))}
          </div>
        </div>

        {isDone && (
          <div style={{ textAlign: 'right', background: timerData.efficiencyScore >= 100 ? '#DCFCE7' : '#FEF2F2', padding: '6px 12px', borderRadius: '10px', border: `1px solid ${timerData.efficiencyScore >= 100 ? '#86EFAC' : '#FECACA'}` }}>
            <span style={{ fontSize: '9px', fontWeight: '800', display: 'block', color: timerData.efficiencyScore >= 100 ? '#166534' : '#991B1B' }}>EFFICIENCY</span>
            <span style={{ fontSize: '16px', fontWeight: '900', color: timerData.efficiencyScore >= 100 ? '#15803D' : '#DC2626' }}>{timerData.efficiencyScore}%</span>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid rgba(0,0,0,0.05)', paddingTop: '10px' }}>
        {/* Late Start Section with Hours Logic */}
        <div style={{ flex: 1, background: timerData.delayMinutes > 0 ? '#FFFBEB' : '#F0FDF4', padding: '6px 10px', borderRadius: '8px', border: `1px solid ${timerData.delayMinutes > 0 ? '#FDE68A' : '#BBF7D0'}` }}>
          <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>
             {job.shift_type === 'afternoon' ? 'Shift: 12 PM' : 'Shift: 7 AM'}
          </span>
          <span style={{ color: timerData.delayMinutes > 0 ? '#D97706' : '#10B981', fontSize: '13px', fontWeight: '800' }}>
            {timerData.delayMinutes > 0 ? `${formatDelay(timerData.delayMinutes)} Late` : 'On Time'}
          </span>
        </div>

        <div style={{ flex: 1, background: '#F8FAFC', padding: '6px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', textAlign: 'center' }}>
          <span style={{ fontSize: '9px', color: '#64748B', fontWeight: '700', textTransform: 'uppercase', display: 'block' }}>Limit (4px)</span>
          <span style={{ color: '#475569', fontSize: '13px', fontWeight: '800' }}>{(parseFloat(job.total_man_hours || 32) / 4)}h</span>
        </div>
      </div>
    </div>
  );
};

  // Helper function to format datetime for input (fixes timezone issue)
  const formatDateTimeForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    // Get local date components
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Helper function to format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // ADD this new function to convert back to UTC for database
  const formatDateTimeForDB = (dateString) => {
    if (!dateString) return null;
    
    // When user selects datetime-local, browser gives us local time
    // We need to send it to backend as-is (backend will handle timezone)
    const date = new Date(dateString);
    
    // Return ISO string (UTC)
    return date.toISOString();
  };

  const handleUpdate = async () => {
  try {
    // Create a copy of editData and convert all datetime fields to proper format
    const payload = { ...editData };
    
    // List of datetime fields to convert
    const datetimeFields = [
      'scheduled_date',
      'reached_location_at', 
      'started_at',
      'before_photos_at',
      'before_photo_at',
      'after_photos_at',
      'after_photo_at',
      'completed_at'
    ];
    
    // Convert each datetime field to ISO format if it exists
    datetimeFields.forEach(field => {
      if (payload[field]) {
        // Only convert if it's not already in ISO format
        if (!payload[field].includes('Z') && !payload[field].includes('+')) {
          payload[field] = formatDateTimeForDB(payload[field]);
        }
      }
    });

    const response = await axios.put(
      `/api/schedule/edit/${editData.schedule_id}`,
      payload,
      config
    );
    
    if (response.data.success) {
      alert("✅ Job updated successfully!");
      setIsEditing(false);
      setDetailsOpen(false);
      fetchJobs();
    }
  } catch (err) {
    console.error(err);
    alert("❌ Error updating job: " + (err.response?.data?.message || err.message));
  }
};

  // Upload image function
  const handleImageUpload = async (file, photoType) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);
    formData.append('schedule_id', editData.schedule_id);
    formData.append('photo_type', photoType);

    try {
      const response = await axios.post('/api/schedule/edit/upload-photo', formData, {
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Parse existing photos
        let existingPhotos = [];
        try {
          existingPhotos = typeof editData[photoType] === 'string' 
            ? JSON.parse(editData[photoType]) 
            : (editData[photoType] || []);
        } catch (e) {
          existingPhotos = [];
        }

        // Add new photo
        existingPhotos.push(response.data.photoPath);

        // Update editData
        setEditData({
          ...editData,
          [photoType]: JSON.stringify(existingPhotos)
        });

        alert("✅ Photo uploaded successfully!");
      }
    } catch (error) {
      console.error("Upload error:", error);
      alert("❌ Error uploading photo: " + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    if (selectedJob) {
      setEditData({ ...selectedJob });
    }
  }, [selectedJob]);

  const [viewMode, setViewMode] = useState("clients");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState("all");
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramClient = searchParams.get("client");
    const paramDate = searchParams.get("date");
    const paramSite = searchParams.get("site");

    if (paramClient && paramDate) {
      const dateObj = new Date(paramDate);
      if (!isNaN(dateObj)) {
        const monthStr = format(dateObj, "yyyy-MM");
        const dateStr = format(dateObj, "yyyy-MM-dd");

        setSelectedClient(paramClient);
        setSelectedMonth(monthStr);
        setSelectedDate(dateStr);
        setViewMode("jobs");

        if (paramSite) {
          setSearchQuery(paramSite);
        }
      }
    } else if (paramClient) {
      setSelectedClient(paramClient);
      setViewMode("months");
    }
  }, [location.search]);

  const cleanPhotoUrl = (url) => {
    if (!url) return "";
    let cleanUrl = url
      .toString()
      .trim()
      .replace(/["\[\]]/g, "")
      .replace(/%22/g, "")
      .replace(/%5D/g, "");
    if (cleanUrl.startsWith("http")) return cleanUrl;
    if (cleanUrl.startsWith("/")) return `https://app.sowashusa.com${cleanUrl}`;
    return `https://app.sowashusa.com/uploads/maintenance/${cleanUrl}`;
  };

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchJobs();
    }, 500);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [filterStatus, dateRange, searchQuery, filterType]);

  const fetchJobs = async () => {
    try {
      setLoading(true);

      const storedUser = localStorage.getItem("user");
      let clientId = null;

      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        clientId = parsedUser.client_id || parsedUser.id;
      }

      const params = {};

      if (clientId) {
        params.client_id = clientId;
      }

      if (filterStatus && filterStatus !== "all") {
        params.status = filterStatus;
      }

      if (filterType && filterType !== "all") {
        params.job_type = filterType;
      }

      if (dateRange && dateRange !== "all") {
        params.dateRange = dateRange;
      }

      if (searchQuery && searchQuery.trim() !== "") {
        params.search = searchQuery.trim();
      }

      const response = await axios.get("/api/schedule/history", {
        params,
        ...config,
      });
      setJobs(response.data.jobs);
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get("/api/schedule/stats", config);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const clientGroups = useMemo(() => {
    const groups = {};
    jobs.forEach((job) => {
      const name = job.client_name || "Unknown Client";
      if (!groups[name]) groups[name] = [];
      groups[name].push(job);
    });
    return groups;
  }, [jobs]);

  const monthGroups = useMemo(() => {
    if (!selectedClient || !clientGroups[selectedClient]) return {};
    const groups = {};
    clientGroups[selectedClient].forEach((job) => {
      const monthKey = format(new Date(job.scheduled_date), "yyyy-MM");
      if (!groups[monthKey]) groups[monthKey] = [];
      groups[monthKey].push(job);
    });
    return groups;
  }, [selectedClient, clientGroups]);

  const dateGroups = useMemo(() => {
    if (!selectedMonth || !monthGroups[selectedMonth]) return {};
    const groups = {};
    monthGroups[selectedMonth].forEach((job) => {
      const dateKey = format(new Date(job.scheduled_date), "yyyy-MM-dd");
      if (!groups[dateKey]) groups[dateKey] = [];
      groups[dateKey].push(job);
    });
    return groups;
  }, [selectedMonth, monthGroups]);

  const currentJobsList = useMemo(() => {
    if (!selectedDate || !dateGroups[selectedDate]) return [];
    return dateGroups[selectedDate];
  }, [selectedDate, dateGroups]);

  const handleClientClick = (clientName) => {
    setSelectedClient(clientName);
    setViewMode("months");
  };

  const handleMonthClick = (monthKey) => {
    setSelectedMonth(monthKey);
    setViewMode("dates");
  };

  const handleDateClick = (dateKey) => {
    setSelectedDate(dateKey);
    setViewMode("jobs");
  };

  const handleBack = () => {
    if (viewMode === "jobs") {
      setViewMode("dates");
      setSelectedDate(null);
    } else if (viewMode === "dates") {
      setViewMode("months");
      setSelectedMonth(null);
    } else if (viewMode === "months") {
      setViewMode("clients");
      setSelectedClient(null);
      setSearchQuery("");
    }
  };

  const handleViewDetails = async (job) => {
    try {
      setDetailsLoading(true);
      setDetailsOpen(true);
      const response = await axios.get(
        `/api/fo-schedules/${job.schedule_id}/detail`,
        config,
      );
      setSelectedJob(response.data.job);
    } catch (error) {
      console.error("Error fetching job details:", error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
      setDetailsOpen(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  // PDF Logic
  const handleDownloadReport = async () => {
    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = 0;

      const colors = {
        primary: [102, 126, 234],
        secondary: [118, 75, 162],
        success: [16, 185, 129],
        warning: [245, 158, 11],
        info: [59, 130, 246],
        dark: [17, 24, 39],
        gray: [107, 114, 128],
        lightGray: [243, 244, 246],
      };

      const checkNewPage = (requiredSpace = 40) => {
        if (yPosition + requiredSpace > pageHeight - 25) {
          pdf.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      const addSectionHeader = (title, iconColor = colors.primary) => {
        checkNewPage(20);
        pdf.setFillColor(...iconColor);
        pdf.circle(margin + 3, yPosition + 3, 3, "F");
        pdf.setTextColor(...colors.dark);
        pdf.setFontSize(14);
        pdf.setFont("helvetica", "bold");
        pdf.text(title, margin + 10, yPosition + 5);
        yPosition += 12;
      };

      const isMaintenance = (job) =>
        job?.job_type?.toLowerCase() === "maintenance";

      const addImageToPDF = async (imageUrl, x, y, width, height) => {
        try {
          if (!imageUrl) return false;
          let cleanUrl = imageUrl.replace(/["\[\]]/g, "").trim();
          if (!cleanUrl.startsWith("http")) {
            cleanUrl = `https://app.sowashusa.com/uploads/${selectedJob && isMaintenance(selectedJob) ? "maintenance" : "job-photos"}/${cleanUrl}`;
          }
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = cleanUrl;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
            setTimeout(resolve, 3000);
          });
          pdf.addImage(img, "JPEG", x, y, width, height);
          return true;
        } catch (error) {
          return false;
        }
      };

      const drawPhotoGrid = async (title, photos, basePath) => {
        if (!photos || photos.length === 0) return;
        checkNewPage(40);
        addSectionHeader(title, colors.info);
        const gap = 10;
        const boxWidth = (pageWidth - 2 * margin - gap) / 2;
        const boxHeight = 60;

        for (let i = 0; i < photos.length; i++) {
          const col = i % 2;
          if (col === 0 && yPosition + boxHeight > pageHeight - 20) {
            pdf.addPage();
            yPosition = margin;
          }
          const xPos = col === 0 ? margin : margin + boxWidth + gap;
          let photoName = photos[i];
          if (photoName.includes("/")) photoName = photoName.split("/").pop();
          photoName = photoName.replace(/["\[\]]/g, "");
          const fullUrl = photoName.startsWith("http")
            ? photoName
            : `${basePath}/${photoName}`;

          pdf.setDrawColor(...colors.lightGray);
          pdf.setLineWidth(0.3);
          pdf.rect(xPos, yPosition, boxWidth, boxHeight);
          await addImageToPDF(
            fullUrl,
            xPos + 2,
            yPosition + 2,
            boxWidth - 4,
            boxHeight - 8,
          );

          pdf.setFontSize(8);
          pdf.setTextColor(...colors.gray);
          pdf.text(
            `${title.replace(" PHOTOS", "")} ${i + 1}`,
            xPos + 2,
            yPosition + boxHeight - 2,
          );

          if (col === 1 || i === photos.length - 1) {
            yPosition += boxHeight + 5;
          }
        }
        yPosition += 5;
      };

      const drawPageHeader = (title, jobData) => {
        yPosition = 0;
        const headerHeight = 65;
        for (let i = 0; i < headerHeight; i++) {
          const ratio = i / headerHeight;
          const r =
            colors.primary[0] +
            (colors.secondary[0] - colors.primary[0]) * ratio;
          const g =
            colors.primary[1] +
            (colors.secondary[1] - colors.primary[1]) * ratio;
          const b =
            colors.primary[2] +
            (colors.secondary[2] - colors.primary[2]) * ratio;
          pdf.setFillColor(r, g, b);
          pdf.rect(0, i, pageWidth, 1, "F");
        }
        pdf.setFillColor(255, 255, 255);
        try {
          const logoImg = new Image();
          logoImg.src = "/sowash-logo.png";
          pdf.addImage(logoImg, "PNG", margin + 60, 5, 60, 15);
        } catch (e) {}

        yPosition = 35;
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(26);
        pdf.setFont("helvetica", "bold");
        pdf.text(title, pageWidth / 2, yPosition, { align: "center" });

        yPosition = 48;
        pdf.setFontSize(11);
        pdf.text(
          `Service #${jobData.service_number}`,
          pageWidth / 2,
          yPosition,
          { align: "center" },
        );
        yPosition += 6;
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.text(
          `Generated on ${format(new Date(), "MMMM dd, yyyy")}`,
          pageWidth / 2,
          yPosition,
          { align: "center" },
        );
        yPosition = 75;
      };

      const drawSiteInfo = (jobData) => {
        const headerY = yPosition;
        addSectionHeader("Site Information", colors.info);
        pdf.setFillColor(...colors.lightGray);
        pdf.roundedRect(
          margin,
          yPosition,
          pageWidth - 2 * margin,
          75,
          3,
          3,
          "F",
        );
        yPosition += 7;

        const leftCol = margin + 5;
        const rightCol = pageWidth / 2 + 2;
        const siteDetails = [
          { label: "SITE NAME", value: jobData.site_name || "N/A" },
          { label: "SERVICE NUMBER", value: `#${jobData.service_number}` },
          { label: "CLIENT NAME", value: jobData.client_name || "N/A" },
          {
            label: "STATUS",
            value: jobData.status.toUpperCase().replace("_", " "),
          },
          {
            label: "JOB TYPE",
            value: isMaintenance(jobData)
              ? "MAINTENANCE SERVICE"
              : "COMMERCIAL CLEANING",
          },
          {
            label: "TEAM LEAD",
            value: jobData.team_lead_name || "Not Assigned",
          },
          { label: "CONTACT PERSON", value: jobData.contact_person || "N/A" },
          { label: "CONTACT NUMBER", value: jobData.contact_number || "N/A" },
        ];
        if (isMaintenance(jobData)) {
          if (jobData.task_name)
            siteDetails.push({ label: "TASK", value: jobData.task_name });
        } else {
          if (jobData.system_size)
            siteDetails.push({
              label: "SYSTEM SIZE",
              value: `${jobData.system_size} kW`,
            });
        }

        siteDetails.forEach((detail, index) => {
          const col = index % 2;
          const row = Math.floor(index / 2);
          const xPos = col === 0 ? leftCol : rightCol;
          const y = yPosition + row * 14;
          pdf.setTextColor(...colors.gray);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7);
          pdf.text(detail.label, xPos, y);
          pdf.setTextColor(...colors.dark);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9);
          pdf.text(detail.value?.toString() || "-", xPos, y + 4);
        });
        yPosition += 68;
        if (jobData.address) {
          pdf.setTextColor(...colors.gray);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(7);
          pdf.text("ADDRESS", leftCol, yPosition);
          pdf.setTextColor(...colors.dark);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9);
          pdf.text(jobData.address, leftCol, yPosition + 4);
          yPosition += 15;
        } else {
          yPosition += 5;
        }
      };

      const drawTimeline = (jobData) => {
        checkNewPage(70);
        addSectionHeader("Job Timeline", colors.warning);
        const timelineEvents = [
          {
            label: "Scheduled",
            date: jobData.scheduled_date,
            color: colors.info,
          },
          { label: "Site Arrival", date: jobData.reached_location_at, color: colors.warning },
          { label: "Work Area Reached", date: jobData.started_at, color: colors.success },
          {
            label: "Work Started",
            date: jobData.before_photos_at || jobData.before_photo_at,
            color: [139, 92, 246],
          },
          {
            label: "Work Completed",
            date: jobData.after_photos_at || jobData.after_photo_at,
            color: [236, 72, 153],
          },
          {
            label: "Site Exit",
            date: jobData.completed_at,
            color: colors.success,
          },
        ];

        timelineEvents.forEach((event, index) => {
          if (event.date) {
            pdf.setFillColor(...event.color);
            pdf.circle(margin + 4, yPosition + 3, 2, "F");
            if (index < timelineEvents.filter((e) => e.date).length - 1) {
              pdf.setDrawColor(...colors.lightGray);
              pdf.setLineWidth(0.5);
              pdf.line(margin + 4, yPosition + 5, margin + 4, yPosition + 11);
            }
            pdf.setTextColor(...event.color);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(10);
            pdf.text(event.label, margin + 10, yPosition + 4.5);
            pdf.setTextColor(...colors.gray);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8);
            pdf.text(
              format(new Date(event.date), "MMM dd, yyyy - hh:mm a"),
              margin + 60,
              yPosition + 4.5,
            );
            yPosition += 10;
          }
        });
        yPosition += 5;
      };

      const renderJobContent = async (jobData) => {
        const title = isMaintenance(jobData)
          ? "Technical Maintenance Report"
          : "Soiling Report";
        drawPageHeader(title, jobData);
        drawSiteInfo(jobData);
        drawTimeline(jobData);

        if (isMaintenance(jobData)) {
          checkNewPage(60);
          addSectionHeader("Maintenance Task Details", colors.secondary);
          if (jobData.task_name) {
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(10);
            pdf.text(jobData.task_name, margin, yPosition + 5);
            yPosition += 8;
          }
          if (jobData.task_description || jobData.maintenance_notes) {
            pdf.setFillColor(253, 244, 255);
            const desc = jobData.task_description || jobData.maintenance_notes;
            const descLines = pdf.splitTextToSize(
              desc,
              pageWidth - 2 * margin - 6,
            );
            const descHeight = Math.max(descLines.length * 5 + 10, 20);
            pdf.roundedRect(
              margin,
              yPosition,
              pageWidth - 2 * margin,
              descHeight,
              2,
              2,
              "F",
            );
            pdf.setDrawColor(192, 38, 211);
            pdf.roundedRect(
              margin,
              yPosition,
              pageWidth - 2 * margin,
              descHeight,
              2,
              2,
              "S",
            );
            pdf.setTextColor(...colors.dark);
            pdf.setFont("helvetica", "normal");
            pdf.text(descLines, margin + 3, yPosition + 7);
            yPosition += descHeight + 10;
          }
          const mChecks = [
            { label: "Safety Check", value: "Passed" },
            { label: "Task Status", value: "Completed" },
            { label: "System Integrity", value: "Verified" },
            { label: "Cleaning", value: "Done" },
          ];
          mChecks.forEach((check, index) => {
            const col = index % 2;
            const row = Math.floor(index / 2);
            const boxWidth = (pageWidth - 2 * margin - 5) / 2;
            const xPos = margin + col * (boxWidth + 5);
            const yPos = yPosition + row * 18;
            pdf.setFillColor(253, 244, 255);
            pdf.roundedRect(xPos, yPos, boxWidth, 15, 2, 2, "F");
            pdf.setDrawColor(...colors.secondary);
            pdf.roundedRect(xPos, yPos, boxWidth, 15, 2, 2, "S");
            pdf.setTextColor(...colors.gray);
            pdf.setFont("helvetica", "bold");
            pdf.text(check.label.toUpperCase(), xPos + 3, yPos + 5);
            pdf.setTextColor(...colors.dark);
            pdf.setFont("helvetica", "normal");
            pdf.text(check.value, xPos + 3, yPos + 10);
          });
          yPosition += Math.ceil(mChecks.length / 2) * 18 + 10;
        }

        if (!isMaintenance(jobData) && jobData.status === "completed") {
          checkNewPage(100);
          addSectionHeader("Field Service Report Details", colors.success);
          const fsrConditions = [
            {
              label: "Cable Condition",
              value: jobData.cable_condition || "N/A",
            },
            { label: "Panel Damage", value: jobData.panel_damage || "N/A" },
            { label: "Inverter Alarm", value: jobData.inverter_alarm || "N/A" },
            {
              label: "Potential Shading",
              value: jobData.potential_shading || "N/A",
            },
            {
              label: "Total Washed Panels",
              value: jobData.total_panels_cleaned || "N/A",
            },
          ];
          fsrConditions.forEach((condition, index) => {
            const col = index % 2;
            const row = Math.floor(index / 2);
            const boxWidth = (pageWidth - 2 * margin - 5) / 2;
            const xPos = margin + col * (boxWidth + 5);
            const yPos = yPosition + row * 18;
            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(xPos, yPos, boxWidth, 15, 2, 2, "F");
            pdf.setDrawColor(...colors.lightGray);
            pdf.setLineWidth(0.3);
            pdf.roundedRect(xPos, yPos, boxWidth, 15, 2, 2, "S");
            pdf.setTextColor(...colors.gray);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(7);
            pdf.text(condition.label.toUpperCase(), xPos + 3, yPos + 5);
            pdf.setTextColor(...colors.dark);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(9);
            const valueLines = pdf.splitTextToSize(
              condition.value?.toString() || "N/A",
              boxWidth - 6,
            );
            pdf.text(valueLines, xPos + 3, yPos + 10);
          });
          yPosition += 60;
        }

        // TOOL BOX TALK AND SIGNATURE SIDE BY SIDE LOGIC
        let tbtArr = [];
        if (jobData.temp_voltage_photos) {
          try { tbtArr = JSON.parse(jobData.temp_voltage_photos); } catch (e) { tbtArr = []; }
        } else if (jobData.temp_voltage_photos_url) {
          tbtArr = [jobData.temp_voltage_photos_url];
        }

        if (jobData.customer_signature || tbtArr.length > 0) {
          checkNewPage(50);
          yPosition += 5;
          
          // Customer Signature (Left Side)
          if (jobData.customer_signature) {
            pdf.setTextColor(...colors.gray);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.text("CUSTOMER SIGNATURE", margin, yPosition);
            
            const sigUrl = jobData.customer_signature.startsWith("http")
              ? jobData.customer_signature
              : `https://app.sowashusa.com/uploads/signatures/${jobData.customer_signature}`;
            await addImageToPDF(sigUrl, margin, yPosition + 6, 80, 25);
          }

          // Tool Box Talk Temperature Photo (Right Side)
          if (tbtArr.length > 0) {
            const rightMargin = (pageWidth / 2) + 5;
            pdf.setTextColor(...colors.gray);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(9);
            pdf.text("TOOL BOX TALK", rightMargin, yPosition);

            let photoName = tbtArr[0];
            if (photoName.includes("/")) photoName = photoName.split("/").pop();
            photoName = photoName.replace(/["\[\]]/g, "");
            const photoBaseUrl = isMaintenance(jobData) ? "https://app.sowashusa.com/uploads/maintenance" : "https://app.sowashusa.com/uploads/job-photos";
            const fullUrl = photoName.startsWith("http") ? photoName : `${photoBaseUrl}/${photoName}`;

            pdf.setDrawColor(...colors.lightGray);
            pdf.setLineWidth(0.3);
            pdf.rect(rightMargin, yPosition + 6, 80, 40);
            await addImageToPDF(fullUrl, rightMargin + 2, yPosition + 8, 76, 36);
          }

          // Add padding for next sections depending on if Tool Box Talk existed
          yPosition += tbtArr.length > 0 ? 55 : 40;
        }

        let beforeArr = [];
        let afterArr = [];
        const photoBaseUrl = isMaintenance(jobData)
          ? "https://app.sowashusa.com/uploads/maintenance"
          : "https://app.sowashusa.com/uploads/job-photos";

        if (jobData.before_photos) {
          try {
            beforeArr = JSON.parse(jobData.before_photos);
          } catch (e) {
            beforeArr = [];
          }
        } else if (jobData.before_photo_url) {
          beforeArr = [jobData.before_photo_url];
        }

        if (jobData.after_photos) {
          try {
            afterArr = JSON.parse(jobData.after_photos);
          } catch (e) {
            afterArr = [];
          }
        } else if (jobData.after_photo_url) {
          afterArr = [jobData.after_photo_url];
        }

        if (beforeArr.length > 0)
          await drawPhotoGrid("BEFORE PHOTOS", beforeArr, photoBaseUrl);
        if (afterArr.length > 0)
          await drawPhotoGrid("AFTER PHOTOS", afterArr, photoBaseUrl);

        const remarks = jobData.remarks || jobData.notes;
        if (remarks) {
          checkNewPage(30);
          yPosition += 3;
          pdf.setTextColor(...colors.gray);
          pdf.setFont("helvetica", "bold");
          pdf.setFontSize(9);
          pdf.text("REMARKS", margin, yPosition);
          yPosition += 6;
          pdf.setFillColor(239, 246, 255);
          const lines = pdf.splitTextToSize(
            remarks,
            pageWidth - 2 * margin - 6,
          );
          const h = lines.length * 5 + 6;
          pdf.roundedRect(
            margin,
            yPosition,
            pageWidth - 2 * margin,
            h,
            2,
            2,
            "F",
          );
          pdf.setDrawColor(59, 130, 246);
          pdf.roundedRect(
            margin,
            yPosition,
            pageWidth - 2 * margin,
            h,
            2,
            2,
            "S",
          );
          yPosition += 5;
          pdf.setTextColor(...colors.dark);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.text(lines, margin + 3, yPosition);
          yPosition += h + 10;
        }
      };

      await renderJobContent(selectedJob);

      const selDateStr = format(
        new Date(selectedJob.scheduled_date),
        "yyyy-MM-dd",
      );
      const mergedJobs = jobs.filter(
        (j) =>
          j.job_type === "maintenance" &&
          j.status === "completed" &&
          format(new Date(j.scheduled_date), "yyyy-MM-dd") === selDateStr &&
          j.unique_id !== selectedJob.unique_id,
      );

      for (const mJob of mergedJobs) {
        pdf.addPage();
        yPosition = 15;
        await renderJobContent(mJob);
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setDrawColor(...colors.lightGray);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        pdf.setFontSize(7);
        pdf.setTextColor(...colors.gray);
        pdf.text("SoWash - Maintenance Services", margin, pageHeight - 8);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 8, {
          align: "center",
        });
        pdf.text(
          format(new Date(), "MMM dd, yyyy"),
          pageWidth - margin,
          pageHeight - 8,
          { align: "right" },
        );
      }

      pdf.save(`Full_Report_${selDateStr}.pdf`);
      alert(`✅ Downloaded! included ${mergedJobs.length} extra reports.`);
    } catch (error) {
      console.error("PDF Error:", error);
      alert("Error: " + error.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return { bg: "#ECFDF5", border: "#10B981", text: "#065F46" };
      case "started":
      case "in_progress":
      case "before_photos":
      case "after_photos":
        return { bg: "#FEF3C7", border: "#F59E0B", text: "#92400E" };
      case "scheduled":
        return { bg: "#EFF6FF", border: "#3B82F6", text: "#1E40AF" };
      default:
        return { bg: "#F3F4F6", border: "#9CA3AF", text: "#374151" };
    }
  };

  const getStatusIcon = (status) =>
    status === "completed"
      ? "✓"
      : ["started", "in_progress", "before_photos", "after_photos"].includes(
          status,
        )
        ? "⏳"
        : "📅";

  const getJobTypeColor = (type) =>
    type === "maintenance"
      ? { bg: "#FDF4FF", border: "#C026D3", text: "#86198F" }
      : { bg: "#DBEAFE", border: "#2563EB", text: "#1E40AF" };

  if (loading && jobs.length === 0)
    return <div className="p-5 text-center">Loading...</div>;

  return (
    <div
      style={{ minHeight: "100vh", background: "#F8F9FA", padding: "24px" }}
    >
      <style>{`
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
                @keyframes slideUp { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
                .job-card { animation: fadeIn 0.5s ease-out; transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .job-card:hover { transform: translateY(-8px); box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important; }
                .stat-card { transition: all 0.3s ease; cursor: pointer; }
                .stat-card:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important; }
                .filter-btn { transition: all 0.2s ease; border: 2px solid transparent; }
                .filter-btn.active { border-color: #667EEA !important; box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1); }
                .nav-card { background: white; border-radius: 16px; padding: 24px; box-shadow: 0 4px 12px rgba(0,0,0,0.06); border: 1px solid #E5E7EB; transition: all 0.2s; cursor: pointer; display: flex; align-items: center; gap: 16px; animation: slideUp 0.3s ease-out; }
                .nav-card:hover { transform: translateY(-3px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); border-color: #667EEA; }
            `}</style>

      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg,#6528a9, #cc2899)",
          borderRadius: "20px",
          padding: "40px",
          marginBottom: "32px",
          boxShadow: "0 10px 30px rgba(102, 126, 234, 0.3)",
          animation: "slideUp 0.5s ease-out",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "20px",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "36px",
                fontWeight: "800",
                color: "white",
                margin: 0,
                marginBottom: "8px",
                letterSpacing: "-0.5px",
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <TimeIcon style={{ fontSize: "40px" }} /> Job History & Analytics
            </h1>
            <p
              style={{
                color: "rgba(255,255,255,0.9)",
                margin: 0,
                fontSize: "16px",
                fontWeight: "400",
              }}
            >
              Track cleaning jobs, maintenance services, and performance metrics
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: "20px",
          marginBottom: "32px",
        }}
      >
        <div
          className="stat-card"
          style={{
            background: "linear-gradient(90deg, #6528a9, #cc2899)",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 8px 20px rgba(102, 126, 234, 0.25)",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              opacity: 0.9,
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            Total Jobs
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              letterSpacing: "-1px",
            }}
          >
            {stats.total}
          </div>
          <div
            style={{
              fontSize: "13px",
              opacity: 0.85,
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <TrendingUpIcon style={{ fontSize: "16px" }} /> All time
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            Completed
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#10B981",
              letterSpacing: "-1px",
            }}
          >
            {stats.completed}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CheckCircleIcon style={{ fontSize: "16px" }} /> Jobs
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            In Progress
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#F59E0B",
              letterSpacing: "-1px",
            }}
          >
            {stats.inProgress}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <ScheduleIcon style={{ fontSize: "16px" }} /> Active
          </div>
        </div>
        <div
          className="stat-card"
          style={{
            background: "white",
            padding: "28px",
            borderRadius: "16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
            border: "2px solid #E9ECEF",
          }}
        >
          <div
            style={{
              fontSize: "14px",
              color: "#6C757D",
              marginBottom: "8px",
              fontWeight: "600",
              textTransform: "uppercase",
            }}
          >
            Scheduled
          </div>
          <div
            style={{
              fontSize: "42px",
              fontWeight: "800",
              color: "#3B82F6",
              letterSpacing: "-1px",
            }}
          >
            {stats.scheduled}
          </div>
          <div
            style={{
              fontSize: "13px",
              color: "#6C757D",
              marginTop: "8px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CalendarIcon style={{ fontSize: "16px" }} /> Upcoming
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div
        style={{
          background: "white",
          padding: "24px",
          borderRadius: "16px",
          marginBottom: "24px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
          border: "1px solid #E9ECEF",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            flexWrap: "wrap",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div style={{ flex: "1", minWidth: "280px", position: "relative" }}>
            <SearchIcon
              style={{
                position: "absolute",
                left: "16px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#9CA3AF",
                fontSize: "20px",
              }}
            />
            <input
              type="text"
              placeholder="Search by site, client, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "14px 16px 14px 48px",
                border: "2px solid #E5E7EB",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: "500",
                outline: "none",
                transition: "all 0.2s",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#667EEA";
                e.target.style.boxShadow = "0 0 0 4px rgba(102, 126, 234, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#E5E7EB";
                e.target.style.boxShadow = "none";
              }}
            />
          </div>
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={() => setFilterType("all")}
              className={`filter-btn ${filterType === "all" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "all" ? "#F3F4F6" : "white",
                color: "#6B7280",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              All Types
            </button>
            <button
              onClick={() => setFilterType("commercial")}
              className={`filter-btn ${filterType === "commercial" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "commercial" ? "#EFF6FF" : "white",
                color: "#2563EB",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              Commercial
            </button>
            <button
              onClick={() => setFilterType("maintenance")}
              className={`filter-btn ${filterType === "maintenance" ? "active" : ""}`}
              style={{
                padding: "10px 20px",
                background: filterType === "maintenance" ? "#FDF4FF" : "white",
                color: "#C026D3",
                borderRadius: "10px",
                fontWeight: "600",
                border: "1px solid #E5E7EB",
                cursor: "pointer",
              }}
            >
              Maintenance
            </button>
          </div>
        </div>
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            borderTop: "1px solid #E5E7EB",
            paddingTop: "16px",
          }}
        >
          <button
            onClick={() => setFilterStatus("all")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "all" ? "#F3F4F6" : "white",
              color: "#4B5563",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <AssignmentIcon style={{ fontSize: "16px" }} /> All Status
          </button>
          <button
            onClick={() => setFilterStatus("completed")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "completed" ? "#ECFDF5" : "white",
              color: "#059669",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CheckCircleIcon style={{ fontSize: "16px" }} /> Completed
          </button>
          <button
            onClick={() => setFilterStatus("started")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "started" ? "#FFFBEB" : "white",
              color: "#D97706",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <ScheduleIcon style={{ fontSize: "16px" }} /> In Progress
          </button>
          <button
            onClick={() => setFilterStatus("scheduled")}
            style={{
              padding: "10px 20px",
              background: filterStatus === "scheduled" ? "#EFF6FF" : "white",
              color: "#2563EB",
              borderRadius: "10px",
              fontWeight: "600",
              border: "1px solid #E5E7EB",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <CalendarIcon style={{ fontSize: "16px" }} /> Scheduled
          </button>
        </div>
      </div>

      {/* BREADCRUMB & BACK BUTTON */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "16px",
          marginBottom: "24px",
        }}
      >
        {viewMode !== "clients" && (
          <button
            onClick={handleBack}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "10px 20px",
              background: "white",
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              cursor: "pointer",
              fontWeight: "600",
              color: "#374151",
              boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
            }}
          >
            <ArrowBackIcon style={{ fontSize: "18px" }} /> Back
          </button>
        )}
        <div
          style={{
            fontSize: "18px",
            fontWeight: "700",
            color: "#111827",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              color: viewMode === "clients" ? "#111827" : "#6B7280",
              cursor: viewMode !== "clients" ? "pointer" : "default",
            }}
            onClick={() => {
              if (viewMode !== "clients") {
                setViewMode("clients");
                setSelectedClient(null);
                setSelectedMonth(null);
                setSearchQuery("");
              }
            }}
          >
            All Clients
          </span>
          {viewMode !== "clients" && (
            <>
              <span>/</span>
              <span
                style={{
                  color: viewMode === "months" ? "#111827" : "#6B7280",
                  cursor: viewMode !== "months" ? "pointer" : "default",
                }}
                onClick={() => {
                  if (viewMode !== "months") {
                    setViewMode("months");
                    setSelectedMonth(null);
                  }
                }}
              >
                {selectedClient}
              </span>
            </>
          )}
          {(viewMode === "dates" || viewMode === "jobs") && (
            <>
              <span>/</span>
              <span
                style={{
                  color: viewMode === "dates" ? "#111827" : "#6B7280",
                  cursor: viewMode === "jobs" ? "pointer" : "default",
                }}
                onClick={() => {
                  if (viewMode === "jobs") {
                    setViewMode("dates");
                    setSelectedDate(null);
                  }
                }}
              >
                {selectedMonth &&
                  format(new Date(selectedMonth + "-01"), "MMMM yyyy")}
              </span>
            </>
          )}
          {viewMode === "jobs" && (
            <>
              <span>/</span>
              <span>{format(new Date(selectedDate), "MMM dd")}</span>
            </>
          )}
        </div>
      </div>

      {/* VIEW 1: CLIENT CARDS */}
      {viewMode === "clients" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(clientGroups).length > 0 ? (
            Object.keys(clientGroups).map((client, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleClientClick(client)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#F0F4FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <BusinessIcon
                    style={{ color: "#3B82F6", fontSize: "28px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {client}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {clientGroups[client].length} Total Jobs
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))
          ) : (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                color: "#6B7280",
              }}
            >
              No clients found.
            </div>
          )}
        </div>
      )}

      {/* VIEW 2: MONTH CARDS */}
      {viewMode === "months" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(monthGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((monthKey, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleMonthClick(monthKey)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#FDF4FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <EventIcon style={{ color: "#C026D3", fontSize: "28px" }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {format(new Date(monthKey + "-01"), "MMMM yyyy")}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {monthGroups[monthKey].length} Jobs
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))}
        </div>
      )}

      {/* VIEW 3: DATE CARDS */}
      {viewMode === "dates" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "24px",
          }}
        >
          {Object.keys(dateGroups)
            .sort((a, b) => new Date(b) - new Date(a))
            .map((date, i) => (
              <div
                key={i}
                className="nav-card"
                onClick={() => handleDateClick(date)}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    borderRadius: "50%",
                    background: "#FFFBEB",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <DateRangeIcon
                    style={{ color: "#F59E0B", fontSize: "28px" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      fontSize: "16px",
                      fontWeight: "700",
                      color: "#111827",
                      margin: 0,
                    }}
                  >
                    {format(new Date(date), "MMMM dd, yyyy")}
                  </h4>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#6B7280",
                      margin: "4px 0 0 0",
                    }}
                  >
                    {dateGroups[date].length} Jobs •{" "}
                    {format(new Date(date), "EEEE")}
                  </p>
                </div>
                <div
                  style={{
                    background: "#F3F4F6",
                    padding: "8px",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowRightIcon style={{ color: "#9CA3AF" }} />
                </div>
              </div>
            ))}
        </div>
      )}

      {/* VIEW 4: JOBS GRID */}
      {viewMode === "jobs" && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
            gap: "24px",
          }}
        >
          {currentJobsList.length > 0 ? (
            currentJobsList.map((job, index) => {
              const statusColors = getStatusColor(job.status);
              const typeColors = getJobTypeColor(job.job_type);
              return (
                <div
                  key={job.unique_id || job.schedule_id || index}
                  className="job-card"
                  style={{
                    background: "white",
                    borderRadius: "20px",
                    padding: "28px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    border: `2px solid ${typeColors.border}`,
                    position: "relative",
                    overflow: "hidden",
                    animationDelay: `${index * 0.1}s`,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "16px",
                      right: "16px",
                      padding: "6px 12px",
                      background: typeColors.bg,
                      color: typeColors.text,
                      borderRadius: "8px",
                      fontSize: "11px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      border: `1px solid ${typeColors.border}`,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      zIndex: 2,
                    }}
                  >
                    {job.job_type === "maintenance" ? (
                      <>
                        <BuildIcon style={{ fontSize: "14px" }} /> Maintenance
                      </>
                    ) : (
                      <>
                        <CleaningIcon style={{ fontSize: "14px" }} /> Commercial
                      </>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "20px",
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <div style={{ flex: 1, paddingRight: "80px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "56px",
                            height: "56px",
                            borderRadius: "14px",
                            background: statusColors.bg,
                            border: `2px solid ${statusColors.border}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "24px",
                            fontWeight: "700",
                            color: statusColors.text,
                            flexShrink: 0,
                          }}
                        >
                          {job.site_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h5
                            style={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#111827",
                              margin: 0,
                              marginBottom: "4px",
                              lineHeight: 1.2,
                            }}
                          >
                            {job.site_name}
                          </h5>
                          <p
                            style={{
                              fontSize: "13px",
                              color: "#6B7280",
                              margin: 0,
                              fontWeight: "500",
                            }}
                          >
                            {job.client_name}
                          </p>
                        </div>
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "6px 14px",
                        background: statusColors.bg,
                        color: statusColors.text,
                        borderRadius: "10px",
                        fontSize: "12px",
                        fontWeight: "700",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        border: `2px solid ${statusColors.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        position: "absolute",
                        top: "60px",
                        right: 0,
                      }}
                    >
                      {getStatusIcon(job.status)} {job.status.replace("_", " ")}
                    </span>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "20px",
                    }}
                  >
                    {job.address && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "36px",
                            height: "36px",
                            borderRadius: "10px",
                            background: "#F0F9FF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <LocationIcon
                            style={{ color: "#3B82F6", fontSize: "18px" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "2px",
                            }}
                          >
                            Location
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {job.location}
                          </div>
                        </div>
                      </div>
                    )}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "10px",
                          background: "#FEF3C7",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <CalendarIcon
                          style={{ color: "#F59E0B", fontSize: "18px" }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "2px",
                          }}
                        >
                          Scheduled
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {format(new Date(job.scheduled_date), "MMM dd, yyyy")}
                        </div>
                      </div>
                    </div>
                  </div>

{/* Task/System and Service Number Grid */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "12px",
    marginBottom: "12px",
    paddingTop: "20px",
    borderTop: "2px solid #F3F4F6",
  }}
>
  <div style={{ background: "#F9FAFB", padding: "14px", borderRadius: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
    <div style={{ fontSize: "10px", color: "#6B7280", fontWeight: "600", textTransform: "uppercase", marginBottom: "4px" }}>
      {job.job_type === "maintenance" ? "Task" : "System"}
    </div>
    <div style={{ fontSize: "16px", fontWeight: "800", color: "#111827" }}>
      {job.job_type === "maintenance" ? (job.task_name || "Maint.") : `${job.system_size} kW`}
    </div>
  </div>

  <div style={{ background: "#F9FAFB", padding: "14px", borderRadius: "12px", border: "1px solid #E5E7EB", textAlign: "center" }}>
    <div style={{ fontSize: "10px", color: "#6B7280", fontWeight: "600", textTransform: "uppercase", marginBottom: "4px" }}>Service</div>
    <div style={{ fontSize: "16px", fontWeight: "800", color: "#111827" }}>#{job.service_number}</div>
  </div>
</div>

{/* Efficiency Timer / Result Section */}
<div style={{ marginBottom: "20px" }}>
  <JobEfficiencyTimer job={job} />
</div>

                  <button
                    onClick={() => handleViewDetails(job)}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background:
                        job.job_type === "maintenance"
                          ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                          : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "15px",
                      fontWeight: "700",
                      cursor: "pointer",
                      boxShadow:
                        job.job_type === "maintenance"
                          ? "0 4px 12px rgba(192, 38, 211, 0.4)"
                          : "0 4px 12px rgba(102, 126, 234, 0.4)",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <ViewIcon style={{ fontSize: "20px" }} /> View Details
                  </button>
                </div>
              );
            })
          ) : (
            <div
              style={{
                gridColumn: "1/-1",
                textAlign: "center",
                padding: "40px",
                color: "#6B7280",
              }}
            >
              No jobs found for this date.
            </div>
          )}
        </div>
      )}

      {/* Job Details Modal */}
      <Modal
        show={detailsOpen}
        onHide={() => {
          setIsEditing(false);
          setDetailsOpen(false);
        }}
        centered
        size="lg"
        style={{ zIndex: 9999 }}
      >
        <div style={{ borderRadius: "20px", overflow: "hidden", border: "none" }}>
          <Modal.Header
            closeButton
            style={{
              background:
                selectedJob?.job_type === "maintenance"
                  ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                  : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
              border: "none",
              padding: "28px 32px",
            }}
          >
            {selectedJob && (
              <Modal.Title
                style={{
                  color: "white",
                  fontSize: "24px",
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {selectedJob.job_type === "maintenance" ? (
                    <BuildIcon style={{ fontSize: "28px" }} />
                  ) : (
                    <DescriptionIcon style={{ fontSize: "28px" }} />
                  )}
                  {selectedJob.site_name}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {!isEditing && (
                    <button
                      onClick={() => {
                        setEditData({ ...selectedJob });
                        setIsEditing(true);
                        
                      }}
                      style={{
                        padding: "6px 16px",
                        background: "rgba(255,255,255,0.2)",
                        color: "white",
                        border: "2px solid rgba(255,255,255,0.4)",
                        borderRadius: "10px",
                        fontSize: "13px",
                        fontWeight: "700",
                        cursor: "pointer",
                      }}
                    >
                      EDIT JOB
                      
                      
                    </button>
                  )}
                  <span
                    style={{
                      padding: "6px 14px",
                      background: "rgba(255,255,255,0.2)",
                      borderRadius: "10px",
                      fontSize: "12px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      border: "2px solid rgba(255,255,255,0.3)",
                    }}
                  >
                    {getStatusIcon(selectedJob.status)}{" "}
                    {selectedJob.status.replace("_", " ")}
                  </span>
                </div>
              </Modal.Title>
            )}
          </Modal.Header>

          {detailsLoading ? (
            <div style={{ padding: "80px", textAlign: "center" }}>
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  border: "4px solid #E9ECEF",
                  borderTop: "4px solid #667EEA",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                  margin: "0 auto 16px",
                }}
              />
              <p style={{ color: "#6C757D", fontWeight: "500" }}>
                Loading details...
              </p>
            </div>
          ) : (
            selectedJob && (
              <>
                <Modal.Body
                  style={{
                    padding: "32px",
                    maxHeight: "70vh",
                    overflowY: "auto",
                  }}
                >
                  {/* SITE INFORMATION */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      padding: "24px",
                      borderRadius: "16px",
                      marginBottom: "24px",
                      border: "2px solid #E5E7EB",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <LocationIcon
                        style={{ color: "#667EEA", fontSize: "22px" }}
                      />{" "}
                      Site Information
                    </h6>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "16px",
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Client
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {selectedJob.client_name}
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Job Type
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          {selectedJob.job_type === "maintenance"
                            ? "Maintenance Service"
                            : "Commercial Cleaning"}
                        </div>
                      </div>
                      {selectedJob.system_size > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            System Size
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.system_size} kW
                          </div>
                        </div>
                      )}
                      <div>
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#6B7280",
                            fontWeight: "600",
                            marginBottom: "4px",
                            textTransform: "uppercase",
                          }}
                        >
                          Service Number
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#111827",
                            fontWeight: "600",
                          }}
                        >
                          #{selectedJob.service_number}
                        </div>
                      </div>
                      {selectedJob.contact_person && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Contact Person
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.contact_person}
                          </div>
                        </div>
                      )}
                      {selectedJob.contact_number && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Contact Number
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.contact_number}
                          </div>
                        </div>
                      )}
                      {selectedJob.team_lead_name && (
                        <div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Team Lead
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.team_lead_name}
                          </div>
                        </div>
                      )}
                      {selectedJob.address && (
                        <div style={{ gridColumn: "1 / -1" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Address
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#111827",
                              fontWeight: "600",
                            }}
                          >
                            {selectedJob.address}, {selectedJob.city}{" "}
                            {selectedJob.state ? ", " + selectedJob.state : ""}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Job Timeline */}
                  <div
                    style={{
                      background: "#F9FAFB",
                      padding: "24px",
                      borderRadius: "16px",
                      marginBottom: "24px",
                      border: "2px solid #E5E7EB",
                    }}
                  >
                    <h6
                      style={{
                        fontSize: "16px",
                        fontWeight: "700",
                        color: "#111827",
                        marginBottom: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <TimeIcon
                        style={{ color: "#667EEA", fontSize: "22px" }}
                      />{" "}
                      Job Timeline
                    </h6>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "start",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "12px",
                            background: "#EFF6FF",
                            border: "2px solid #3B82F6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <CalendarIcon
                            style={{ color: "#3B82F6", fontSize: "20px" }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#3B82F6",
                              marginBottom: "2px",
                            }}
                          >
                            Scheduled
                          </div>
                          {isEditing ? (
                            <input
                              type="date"
                              className="form-control"
                              value={formatDateForInput(editData?.scheduled_date)}
                              onChange={(e) => setEditData({ ...editData, scheduled_date: e.target.value })}
                              style={{ padding: "4px 8px", fontSize: "12px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                            />
                          ) : (
                            <div style={{ fontSize: "12px", color: "#6B7280" }}>
                              {format(
                                new Date(selectedJob.scheduled_date),
                                "MMM dd, yyyy",
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {[
                        { label: "Site Arrival", key: "reached_location_at", icon: <LocationIcon style={{ color: "#F59E0B" }} />, bg: "#FEF3C7", border: "#F59E0B" },
                        { label: "Work Area Reached", key: "started_at", icon: <ScheduleIcon style={{ color: "#10B981" }} />, bg: "#ECFDF5", border: "#10B981" },
                        { label: "Work Started", key: "before_photos_at", fallbackKey: "before_photo_at", icon: <PhotoIcon style={{ color: "#A855F7" }} />, bg: "#F3E8FF", border: "#A855F7" },
                        { label: "Work Completed", key: "after_photos_at", fallbackKey: "after_photo_at", icon: <PhotoIcon style={{ color: "#EC4899" }} />, bg: "#FCE7F3", border: "#EC4899" },
                        { label: "Site Exit", key: "completed_at", icon: <CheckCircleIcon style={{ color: "#10B981" }} />, bg: "#ECFDF5", border: "#10B981" }
                      ].map((item) => {
                        const timestamp = selectedJob[item.key] || selectedJob[item.fallbackKey];
                        const editTimestamp = editData?.[item.key] || editData?.[item.fallbackKey];
                        
                        if (!timestamp && !isEditing) return null;
                        
                        return (
                          <div key={item.key} style={{ display: "flex", alignItems: "start", gap: "12px" }}>
                            <div style={{ width: "40px", height: "40px", borderRadius: "12px", background: item.bg, border: `2px solid ${item.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              {item.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "13px", fontWeight: "700", color: item.border, marginBottom: "2px" }}>{item.label}</div>
                              {isEditing ? (
                                <input
                                  type="datetime-local"
                                  className="form-control"
                                  value={formatDateTimeForInput(editTimestamp)}
                                  onChange={(e) => setEditData({ ...editData, [item.key]: e.target.value })}
                                  style={{ padding: "4px 8px", fontSize: "12px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                                />
                              ) : (
                                <div style={{ fontSize: "12px", color: "#6B7280" }}>
                                  {timestamp ? format(new Date(timestamp), "MMM dd, yyyy • hh:mm a") : "Pending"}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Photos */}
                  {(selectedJob.temp_voltage_photos || selectedJob.before_photos || selectedJob.after_photos || isEditing) && (
                    <div
                      style={{
                        background: "#F9FAFB",
                        padding: "24px",
                        borderRadius: "16px",
                        marginBottom: "24px",
                        border: "2px solid #E5E7EB",
                      }}
                    >
                      <h6
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          color: "#111827",
                          marginBottom: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <PhotoIcon
                          style={{ color: "#667EEA", fontSize: "22px" }}
                        />{" "}
                        Photo Gallery
                      </h6>
                      
                      {/* MODIFIED: Added temp_voltage_photos in the photo gallery loop */}
                      {['temp_voltage_photos', 'before_photos', 'after_photos'].map((photoType) => {
                        const photosRaw = isEditing ? editData?.[photoType] : selectedJob[photoType];
                        let photos = [];
                        try { 
                          photos = typeof photosRaw === 'string' ? JSON.parse(photosRaw) : (photosRaw || []); 
                        } catch (e) { 
                          photos = []; 
                        }

                        let displayTitle = photoType.replace(/_/g, ' ');
                        if (photoType === 'temp_voltage_photos') {
                          displayTitle = "Tool Box Talk ";
                        }

                        return (
                          <div key={photoType} style={{ marginBottom: "20px" }}>
                            <div style={{ fontSize: "13px", fontWeight: "700", color: "#6B7280", marginBottom: "12px", textTransform: 'capitalize' }}>
                              {displayTitle}
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: "12px" }}>
                              {photos.map((photo, index) => (
                                <div key={index} style={{ position: 'relative' }}>
                                  <img
                                    src={`https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`}
                                    alt={`${photoType} ${index + 1}`}
                                    style={{ width: "100%", height: "150px", objectFit: "cover", borderRadius: "12px", cursor: isEditing ? "default" : "pointer", border: "2px solid #E5E7EB" }}
                                    onClick={() => !isEditing && window.open(`https://app.sowashusa.com/uploads/job-photos/${photo.split("/").pop()}`, "_blank")}
                                  />
                                  {isEditing && (
                                    <button
                                      onClick={() => {
                                        const updated = photos.filter((_, i) => i !== index);
                                        setEditData({ ...editData, [photoType]: JSON.stringify(updated) });
                                      }}
                                      style={{ position: 'absolute', top: '5px', right: '5px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontWeight: '700' }}
                                    >✕</button>
                                  )}
                                </div>
                              ))}
                              {isEditing && (
                                <label style={{ height: '150px', border: '2px dashed #667EEA', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#667EEA', fontSize: '12px', fontWeight: '700', flexDirection: 'column', gap: '4px' }}>
                                  + Add Photo
                                  <input 
                                    type="file" 
                                    accept="image/*"
                                    hidden 
                                    onChange={(e) => {
                                      if (e.target.files && e.target.files[0]) {
                                        handleImageUpload(e.target.files[0], photoType);
                                      }
                                    }} 
                                  />
                                </label>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* CONDITIONAL REPORT SECTION */}
                  {selectedJob.status === "completed" &&
                    (selectedJob.job_type === "maintenance" ? (
                      // MAINTENANCE REPORT
                      <div
                        style={{
                          background: "#FDF4FF",
                          padding: "24px",
                          borderRadius: "16px",
                          marginBottom: "24px",
                          border: "2px solid #C026D3",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#86198F",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <EngineeringIcon
                            style={{ color: "#C026D3", fontSize: "22px" }}
                          />{" "}
                          Maintenance Report
                        </h6>

                        <div style={{ marginBottom: "16px" }}>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "4px",
                              textTransform: "uppercase",
                            }}
                          >
                            Task Name
                          </div>
                          {isEditing ? (
                            <input
                              type="text"
                              className="form-control"
                              value={editData?.task_name || ""}
                              onChange={(e) => setEditData({ ...editData, task_name: e.target.value })}
                              style={{ fontSize: "15px", fontWeight: "700", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: "15px",
                                color: "#111827",
                                fontWeight: "700",
                              }}
                            >
                              {selectedJob.task_name || "General Maintenance"}
                            </div>
                          )}
                        </div>

                        {(selectedJob.task_description || selectedJob.maintenance_notes || isEditing) && (
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "8px",
                                textTransform: "uppercase",
                              }}
                            >
                              Description & Notes
                            </div>
                            {isEditing ? (
                              <textarea
                                className="form-control"
                                value={editData?.task_description || editData?.maintenance_notes || ""}
                                onChange={(e) => setEditData({ ...editData, task_description: e.target.value })}
                                rows={4}
                                style={{ fontSize: "14px", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                }}
                              >
                                {selectedJob.task_description ||
                                  selectedJob.maintenance_notes}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Static Checks for Maintenance Visualization */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                          }}
                        >
                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "10px",
                              border: "1px solid #E5E7EB",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#4B5563",
                              }}
                            >
                              Safety Check
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#059669",
                                background: "#D1FAE5",
                                padding: "2px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              PASSED
                            </span>
                          </div>
                          <div
                            style={{
                              background: "white",
                              padding: "12px",
                              borderRadius: "10px",
                              border: "1px solid #E5E7EB",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#4B5563",
                              }}
                            >
                              System Status
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "700",
                                color: "#059669",
                                background: "#D1FAE5",
                                padding: "2px 8px",
                                borderRadius: "6px",
                              }}
                            >
                              VERIFIED
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // COMMERCIAL FSR
                      <div
                        style={{
                          background: "#F9FAFB",
                          padding: "24px",
                          borderRadius: "16px",
                          marginBottom: "24px",
                          border: "2px solid #E5E7EB",
                        }}
                      >
                        <h6
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#111827",
                            marginBottom: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <DescriptionIcon
                            style={{ color: "#667EEA", fontSize: "22px" }}
                          />{" "}
                          Field Service Report
                        </h6>
                        
                        {/* Main Fields Grid */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          {/* Cable Condition */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Cable Condition
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.cable_condition || ""}
                                onChange={(e) => setEditData({ ...editData, cable_condition: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Good">Good</option>
                                <option value="Bad">Bad</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.cable_condition || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Cable Quantity */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Cable Quantity
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.cable_quantity || ""}
                                onChange={(e) => setEditData({ ...editData, cable_quantity: e.target.value })}
                                placeholder="e.g. 10 meters"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.cable_quantity || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Panel Damage */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Panel Damage
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.panel_damage || ""}
                                onChange={(e) => setEditData({ ...editData, panel_damage: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.panel_damage || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Panel Brand */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Panel Brand
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.panel_brand || ""}
                                onChange={(e) => setEditData({ ...editData, panel_brand: e.target.value })}
                                placeholder="e.g. Canadian Solar"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.panel_brand || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Inverter Alarm */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Inverter Alarm
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.inverter_alarm || ""}
                                onChange={(e) => setEditData({ ...editData, inverter_alarm: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.inverter_alarm || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Alarm Code */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Alarm Code
                            </div>
                            {isEditing ? (
                              <input
                                type="text"
                                className="form-control"
                                value={editData?.alarm_code || ""}
                                onChange={(e) => setEditData({ ...editData, alarm_code: e.target.value })}
                                placeholder="e.g. E001"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.alarm_code || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Potential Shading */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Potential Shading
                            </div>
                            {isEditing ? (
                              <select
                                className="form-control"
                                value={editData?.potential_shading || ""}
                                onChange={(e) => setEditData({ ...editData, potential_shading: e.target.value })}
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              >
                                <option value="">Select...</option>
                                <option value="Yes">Yes</option>
                                <option value="No">No</option>
                              </select>
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.potential_shading || "N/A"}
                              </div>
                            )}
                          </div>

                          {/* Total Panels Cleaned */}
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Total Panels Cleaned
                            </div>
                            {isEditing ? (
                              <input
                                type="number"
                                className="form-control"
                                value={editData?.total_panels_cleaned || ""}
                                onChange={(e) => setEditData({ ...editData, total_panels_cleaned: e.target.value })}
                                placeholder="e.g. 150"
                                style={{ 
                                  fontSize: "14px", 
                                  fontWeight: "700", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#111827",
                                  fontWeight: "700",
                                }}
                              >
                                {selectedJob.total_panels_cleaned || "N/A"}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Shading Details */}
                        {(selectedJob.potential_shading === "Yes" || (isEditing && editData?.potential_shading === "Yes")) && (
                          <div
                            style={{
                              background: "white",
                              padding: "16px",
                              borderRadius: "12px",
                              border: "1px solid #E5E7EB",
                              marginBottom: "12px"
                            }}
                          >
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#6B7280",
                                fontWeight: "600",
                                marginBottom: "6px",
                                textTransform: "uppercase",
                              }}
                            >
                              Shading Details
                            </div>
                            {isEditing ? (
                              <textarea
                                className="form-control"
                                value={editData?.shading_details || ""}
                                onChange={(e) => setEditData({ ...editData, shading_details: e.target.value })}
                                rows={3}
                                placeholder="Describe shading issues..."
                                style={{ 
                                  fontSize: "14px", 
                                  padding: "8px", 
                                  borderRadius: "8px", 
                                  border: "1px solid #D1D5DB" 
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  fontSize: "14px",
                                  color: "#374151",
                                  lineHeight: "1.6",
                                }}
                              >
                                {selectedJob.shading_details || "No details provided"}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Boolean Checkboxes */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "12px",
                            marginTop: "16px",
                          }}
                        >
                          {[
                            { label: "Rusting", key: "rusting", icon: "🔧" },
                            { label: "Bird Dropping", key: "bird_dropping", icon: "🦅" },
                            { label: "Moss & Debris", key: "mos_and_debris", icon: "🍃" },
                            { label: "Earthing", key: "earthing", icon: "⚡" },
                          ].map((field) => (
                            <div
                              key={field.key}
                              style={{
                                background: isEditing && editData?.[field.key] 
                                  ? "#ECFDF5" 
                                  : selectedJob[field.key] 
                                  ? "#ECFDF5" 
                                  : "white",
                                padding: "12px 16px",
                                borderRadius: "12px",
                                border: isEditing && editData?.[field.key]
                                  ? "2px solid #10B981"
                                  : selectedJob[field.key]
                                  ? "2px solid #10B981"
                                  : "1px solid #E5E7EB",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                cursor: isEditing ? "pointer" : "default",
                              }}
                              onClick={() => {
                                if (isEditing) {
                                  setEditData({ 
                                    ...editData, 
                                    [field.key]: !editData?.[field.key] 
                                  });
                                }
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <span style={{ fontSize: "18px" }}>{field.icon}</span>
                                <span
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    color: "#4B5563",
                                  }}
                                >
                                  {field.label}
                                </span>
                              </div>
                              {isEditing ? (
                                <input
                                  type="checkbox"
                                  checked={editData?.[field.key] || false}
                                  onChange={(e) => setEditData({ ...editData, [field.key]: e.target.checked })}
                                  style={{ 
                                    width: "18px", 
                                    height: "18px", 
                                    cursor: "pointer" 
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <span
                                  style={{
                                    fontSize: "18px",
                                    fontWeight: "700",
                                    color: selectedJob[field.key] ? "#10B981" : "#9CA3AF",
                                  }}
                                >
                                  {selectedJob[field.key] ? "✓" : "✗"}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Additional Notes */}
                        <div
                          style={{
                            background: "white",
                            padding: "16px",
                            borderRadius: "12px",
                            border: "1px solid #E5E7EB",
                            marginTop: "16px"
                          }}
                        >
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#6B7280",
                              fontWeight: "600",
                              marginBottom: "6px",
                              textTransform: "uppercase",
                            }}
                          >
                            Additional Notes
                          </div>
                          {isEditing ? (
                            <textarea
                              className="form-control"
                              value={editData?.additional_notes || ""}
                              onChange={(e) => setEditData({ ...editData, additional_notes: e.target.value })}
                              rows={3}
                              placeholder="Any additional observations..."
                              style={{ 
                                fontSize: "14px", 
                                padding: "8px", 
                                borderRadius: "8px", 
                                border: "1px solid #D1D5DB" 
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                fontSize: "14px",
                                color: "#374151",
                                lineHeight: "1.6",
                              }}
                            >
                              {selectedJob.additional_notes || "No additional notes"}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                  {/* Customer Notes */}
                  {(selectedJob.notes || isEditing) && (
                    <div
                      style={{
                        background: "#FFFBEB",
                        padding: "20px",
                        borderRadius: "16px",
                        border: "2px solid #FCD34D",
                        marginBottom: "16px",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#92400E",
                          marginBottom: "8px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        <NotesIcon /> Notes
                      </div>
                      {isEditing ? (
                        <textarea
                          className="form-control"
                          value={editData?.notes || ""}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={3}
                          style={{ fontSize: "14px", padding: "8px", borderRadius: "8px", border: "1px solid #D1D5DB" }}
                        />
                      ) : (
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#78350F",
                            lineHeight: "1.6",
                          }}
                        >
                          {selectedJob.notes}
                        </div>
                      )}
                    </div>
                  )}

                </Modal.Body>

                <Modal.Footer
                  style={{
                    borderTop: "2px solid #F3F4F6",
                    padding: "20px 32px",
                    background: "#FAFBFC",
                  }}
                >
                  <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                    {isEditing ? (
                      <>
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditData(null);
                          }}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "white",
                            color: "#374151",
                            border: "2px solid #E5E7EB",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleUpdate}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "#10B981",
                            color: "white",
                            border: "none",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Save Changes
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setDetailsOpen(false)}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background: "white",
                            color: "#374151",
                            border: "2px solid #E5E7EB",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                          }}
                        >
                          Close
                        </button>
                        <button
                          onClick={handleDownloadReport}
                          style={{
                            flex: 1,
                            padding: "14px",
                            background:
                              selectedJob.job_type === "maintenance"
                                ? "linear-gradient(135deg, #C026D3 0%, #86198F 100%)"
                                : "linear-gradient(135deg, #667EEA 0%, #764BA2 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "12px",
                            fontSize: "15px",
                            fontWeight: "600",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                          }}
                        >
                          <DownloadIcon style={{ fontSize: "20px" }} /> Download
                          Report
                        </button>
                      </> 
                    )}
                  </div>
                </Modal.Footer>
              </>
            )
          )}
        </div>
      </Modal>
    </div>
  );
};

export default JobHistory;